import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { KpiTrack, StrategyConfig, StrategyLevel, StrategyNode, StrategyOverview, StrategyStatus, StrategyTree } from '@mnc/contracts';
import { api } from '../../lib/api';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';

export const STATUSES: StrategyStatus[] = ['planned', 'in_progress', 'at_risk', 'delayed', 'completed', 'cancelled'];
export const LEVELS: StrategyLevel[] = ['strategy', 'sector', 'objective', 'initiative', 'project', 'sub_project'];

export function statusTone(s: StrategyStatus): 'neutral' | 'brand' | 'warn' | 'danger' | 'info' {
  switch (s) {
    case 'completed':
      return 'brand';
    case 'in_progress':
      return 'info';
    case 'at_risk':
      return 'warn';
    case 'delayed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Solid colour classes per status (bars, dots, stacked distributions). */
export const STATUS_FILL: Record<StrategyStatus, string> = {
  planned: 'bg-neutral-400',
  in_progress: 'bg-blue-500',
  at_risk: 'bg-amber-500',
  delayed: 'bg-red-500',
  completed: 'bg-brand-600',
  cancelled: 'bg-neutral-300',
};

/** Level colour coding (MRLCO UX §6.2, extended with the strategy root and sectors). */
export const LEVEL_TONE: Record<StrategyLevel, string> = {
  strategy: 'bg-brand-700 text-white',
  sector: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  objective: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  initiative: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  project: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  sub_project: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
};

export function trackTone(t: KpiTrack): 'neutral' | 'brand' | 'warn' | 'danger' | 'info' {
  return t === 'achieved' ? 'brand' : t === 'on_track' ? 'info' : t === 'behind' ? 'danger' : 'neutral';
}

export function StatusBadge({ status }: { status: StrategyStatus }) {
  const { t } = useTranslation();
  return <Badge tone={statusTone(status)}>{t(`strategy.statuses.${status}`)}</Badge>;
}

export function LevelBadge({ level, config }: { level: StrategyLevel; config: StrategyConfig | undefined }) {
  const label = useLevelLabel(config);
  return <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', LEVEL_TONE[level])}>{label(level)}</span>;
}

/** Thin progress bar; `null` renders a muted "no target" track. */
export function ProgressBar({ value, className }: { value: number | null; className?: string }) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
        <div className={cn('h-full rounded-full', value === null ? 'bg-transparent' : value >= 100 ? 'bg-brand-600' : 'bg-blue-500')} style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
      <span className="w-10 text-xs tabular-nums text-neutral-500">{value === null ? '—' : `${Math.round(value)}%`}</span>
    </div>
  );
}

/** Donut ring (MRLCO's KPI donut) with the value in the middle. */
export function ProgressRing({ value, size = 48, stroke = 5, label, className }: { value: number | null; size?: number; stroke?: number; label?: string; className?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value ?? 0));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={cn('shrink-0', className)} role="img" aria-label={label ?? (value === null ? '—' : `${Math.round(value)}%`)}>
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" className="stroke-neutral-200 dark:stroke-neutral-800" />
      {value !== null && (
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`} className={v >= 100 ? 'stroke-brand-600' : 'stroke-blue-500'} />
      )}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-current font-semibold" style={{ fontSize: Math.max(9, size / 4.5) }}>
        {label ?? (value === null ? '—' : `${Math.round(value)}%`)}
      </text>
    </svg>
  );
}

/** Stacked bar of a status distribution with a legend. */
export function StatusDistribution({ counts, className }: { counts: Partial<Record<StrategyStatus, number>>; className?: string }) {
  const { t } = useTranslation();
  const total = STATUSES.reduce((a, s) => a + (counts[s] ?? 0), 0);
  if (total === 0) return null;
  return (
    <div className={className}>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
        {STATUSES.filter((s) => (counts[s] ?? 0) > 0).map((s) => <div key={s} className={STATUS_FILL[s]} style={{ width: `${((counts[s] ?? 0) / total) * 100}%` }} title={`${t(`strategy.statuses.${s}`)}: ${counts[s]}`} />)}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-neutral-500">
        {STATUSES.filter((s) => (counts[s] ?? 0) > 0).map((s) => <span key={s} className="inline-flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', STATUS_FILL[s])} />{t(`strategy.statuses.${s}`)} {counts[s]}</span>)}
      </div>
    </div>
  );
}

export function useStrategyTree(enabled = true) {
  return useQuery({ queryKey: ['strategy', 'tree'], queryFn: () => api.get<StrategyTree>('/api/strategy/tree'), enabled, staleTime: 15_000 });
}

export function useStrategyConfig(enabled = true) {
  return useQuery({ queryKey: ['strategy', 'config'], queryFn: () => api.get<StrategyConfig>('/api/strategy/config'), enabled, staleTime: 60_000 });
}

export function useStrategyOverview(nodeId: string | undefined) {
  return useQuery({ queryKey: ['strategy', 'overview', nodeId], queryFn: () => api.get<StrategyOverview>(`/api/strategy/nodes/${nodeId}/overview`), enabled: Boolean(nodeId), staleTime: 15_000 });
}

/** Level labels from the instance configuration (bilingual, singular or plural). */
export function useLevelLabel(config: StrategyConfig | undefined) {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const map = new Map((config?.levels ?? []).map((l) => [l.code, l]));
    return (level: StrategyLevel | string, plural = false): string => {
      const l = map.get(level as StrategyLevel);
      if (!l) return level;
      return plural ? pick(i18n.language, l.plural_en, l.plural_ar) : pick(i18n.language, l.name_en, l.name_ar);
    };
  }, [config, i18n.language]);
}

/** The level that follows `level` in the enabled cascade, or null for the leaf. */
export function childLevelOf(config: StrategyConfig | undefined, level: StrategyLevel): StrategyLevel | null {
  if (!config) return null;
  const i = config.enabled.indexOf(level);
  return i >= 0 && i < config.enabled.length - 1 ? config.enabled[i + 1]! : null;
}

/** Enabled levels strictly below `level`. */
export function levelsBelow(config: StrategyConfig | undefined, level: StrategyLevel): StrategyLevel[] {
  if (!config) return [];
  const i = config.enabled.indexOf(level);
  return i >= 0 ? config.enabled.slice(i + 1) : [];
}

export function childrenMapOf(nodes: StrategyNode[]): Map<string | null, StrategyNode[]> {
  const map = new Map<string | null, StrategyNode[]>();
  for (const n of nodes) map.set(n.parent_id, [...(map.get(n.parent_id) ?? []), n]);
  return map;
}

/** All descendants of `id` within `nodes` (any depth). */
export function descendantsOf(nodes: StrategyNode[], id: string): StrategyNode[] {
  const children = childrenMapOf(nodes);
  const out: StrategyNode[] = [];
  const stack = [...(children.get(id) ?? [])];
  while (stack.length) {
    const n = stack.pop()!;
    out.push(n);
    for (const c of children.get(n.id) ?? []) stack.push(c);
  }
  return out;
}

export function nodeTitle(locale: string, n: Pick<StrategyNode, 'code' | 'name_en' | 'name_ar'>): string {
  return `${n.code} · ${pick(locale, n.name_en, n.name_ar)}`;
}

export function fmtDate(iso: string | null | undefined, locale: string): string {
  return iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}
